import { InternalAccessor } from '@billy/design-system'

import { roundAmount } from '@features/reconciliation/utils/roundAmount'
import type { Account } from '@modules-deprecated/app/accounts/types'

import type { ReviewEntryItem } from '../types/reviewEntryItem'
import type { ReviewEntryLine } from '../types/reviewEntryLine'
import type { ReviewEntryTableRow } from '../types/reviewEntryTableRow'
import { formatReviewEntryAccountLabel } from './formatReviewEntryAccountLabel'
import { getReviewEntryId } from './getReviewEntryId'
import { getReviewEntryReceiptDate } from './getReviewEntryReceiptDate'
import { isReviewEntryProcessingStatus } from './isReviewEntryProcessingStatus'

const getAccount = (line: ReviewEntryLine, accountById: Map<string, Account>): string | undefined => {
  const account = line.accountId ? accountById.get(line.accountId) : undefined

  return formatReviewEntryAccountLabel({
    account: line.account,
    accountName: account?.name ?? line.accountName,
    accountNumber: line.accountNumber ?? account?.accountNo?.toString(),
  })
}

const getDocumentAmountInclVat = (entry: ReviewEntryItem): number | undefined => {
  if (entry.amountInclVat != null) {
    return entry.amountInclVat
  }

  const lineAmounts = (entry.entryLines ?? [])
    .map((line) => line.amountInclVat ?? line.amountInclTax)
    .filter((amount): amount is number => amount != null)

  if (!lineAmounts.length) {
    return undefined
  }

  return roundAmount(lineAmounts.reduce((sum, amount) => sum + amount, 0))
}

const createDocumentOnlyRow = (entry: ReviewEntryItem, entryId: string): ReviewEntryTableRow => {
  const status = entry.documentStatus ?? entry.status
  const isProcessing = isReviewEntryProcessingStatus(status)

  return {
    ...(isProcessing ? { [InternalAccessor.Disabled]: true } : {}),
    amountInclVat: getDocumentAmountInclVat(entry),
    billId: entry.documentName,
    currencyId: entry.currencyId,
    date: getReviewEntryReceiptDate(entry.entryDate, entry.uploadedAt),
    description: isProcessing ? entry.documentName : undefined,
    documentId: entry.documentId,
    entryId,
    id: entryId,
    questions: entry.questions ?? [],
    showDocumentLevelFields: true,
    status,
    supplierName: entry.supplierName,
  }
}

interface CreateMultiLineHeaderRowParams {
  documentAmountInclVat: number | undefined
  entry: ReviewEntryItem
  entryId: string
}

const createMultiLineHeaderRow = ({
  documentAmountInclVat,
  entry,
  entryId,
}: CreateMultiLineHeaderRowParams): ReviewEntryTableRow => ({
  amountInclVat: documentAmountInclVat,
  billId: entry.documentName,
  currencyId: entry.currencyId,
  date: getReviewEntryReceiptDate(entry.entryDate, entry.uploadedAt),
  documentId: entry.documentId,
  entryId,
  id: `${entryId}-header`,
  isMultiLineHeader: true,
  questions: entry.questions ?? [],
  showDocumentLevelFields: true,
  status: entry.documentStatus ?? entry.status,
  supplierName: entry.supplierName,
})

interface CreateLineRowParams {
  accountById: Map<string, Account>
  entry: ReviewEntryItem
  entryId: string
  line: ReviewEntryLine
  lineIndex: number
}

const createLineRow = ({ accountById, entry, entryId, line, lineIndex }: CreateLineRowParams): ReviewEntryTableRow => ({
  account: getAccount(line, accountById),
  amountExclVat: line.amountExclVat ?? line.amountExclTax,
  amountInclVat: line.amountInclVat ?? line.amountInclTax,
  billId: entry.documentName,
  currencyId: line.currencyId ?? entry.currencyId,
  description: line.description,
  documentId: entry.documentId,
  entryId,
  id: `${entryId}-line-${lineIndex}`,
  isMultiLineHeader: false,
  questions: [],
  showDocumentLevelFields: false,
  vatAmount: line.vatAmount ?? line.taxAmount,
  vatRate: line.taxRateLabel,
})

interface CreateSingleLineRowParams {
  accountById: Map<string, Account>
  documentAmountInclVat: number | undefined
  entry: ReviewEntryItem
  entryId: string
  line: ReviewEntryLine
}

const createSingleLineRow = ({
  accountById,
  documentAmountInclVat,
  entry,
  entryId,
  line,
}: CreateSingleLineRowParams): ReviewEntryTableRow => ({
  account: getAccount(line, accountById),
  amountExclVat: line.amountExclVat ?? line.amountExclTax,
  amountInclVat: line.amountInclVat ?? line.amountInclTax ?? documentAmountInclVat,
  billId: entry.documentName,
  currencyId: line.currencyId ?? entry.currencyId,
  date: getReviewEntryReceiptDate(entry.entryDate, entry.uploadedAt),
  description: line.description,
  documentId: entry.documentId,
  entryId,
  id: `${entryId}-line-0`,
  isMultiLineHeader: false,
  questions: entry.questions ?? [],
  showDocumentLevelFields: true,
  status: entry.documentStatus ?? entry.status,
  supplierName: entry.supplierName,
  vatAmount: line.vatAmount ?? line.taxAmount,
  vatRate: line.taxRateLabel,
})

export const flattenReviewEntriesToTableRows = (
  entries: ReviewEntryItem[],
  accounts: Account[] = [],
): ReviewEntryTableRow[] => {
  const accountById = new Map(accounts.map((account) => [account.id, account]))

  return entries.flatMap((entry, entryIndex) => {
    const entryId = getReviewEntryId(entry, entryIndex)
    const entryLines = entry.entryLines ?? []

    if (!entryLines.length) {
      return [createDocumentOnlyRow(entry, entryId)]
    }

    const documentAmountInclVat = getDocumentAmountInclVat(entry)

    if (entryLines.length === 1) {
      return [createSingleLineRow({ accountById, documentAmountInclVat, entry, entryId, line: entryLines[0] })]
    }

    return [
      createMultiLineHeaderRow({ documentAmountInclVat, entry, entryId }),
      ...entryLines.map((line, lineIndex) => createLineRow({ accountById, entry, entryId, line, lineIndex })),
    ]
  })
}
